import { Component, Input, ViewChild } from '@angular/core';
import { ISmartCanvasClickedNodeEvent, ISmartCanvasOptions } from '../../../shared/models/interfaces/smart-canvas.interface';
import { GroupConfiguration } from '../../../shared/models/group-keys.model';
import { IFileDocument, IFilePageAndWords } from '../../../shared/models/interfaces/files.interface';
import { faFileCircleExclamation, faFileSlash } from '@fortawesome/pro-duotone-svg-icons';
import { faCircleNotch } from '@fortawesome/pro-thin-svg-icons';
import { ClassificationParameters, RobotPreview } from 'src/modules/shared/models/robots.model';
import { FilesService } from 'src/modules/shared/services/files.service';
import formatter from 'xml-formatter';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EventEmitterService } from 'src/modules/shared/services/event-emitter.service';
import { FilesHelper } from 'src/modules/shared/helpers/files.helper';

@Component({
  selector: 'hcr-file-reader',
  templateUrl: './file-reader.component.html',
  styleUrls: ['./file-reader.component.sass'],
})
export class FileReaderComponent {
  @Input() selectedFilePagesAndWords: IFilePageAndWords[];
  @Input() comparingFiles: boolean;
  @Input() loadingFile: boolean;
  @Input() errorFile?: boolean;
  @Input() selectedRobot: ClassificationParameters;
  @Input() selectedGroupConfiguration: GroupConfiguration;
  @Input() selectedRobotFilePreview: RobotPreview;
  @Input() smartCanvasOptions: ISmartCanvasOptions;
  @ViewChild('codeeditor') private _codeEditor;
  @Input() set selectedFile(file: IFileDocument) {
    this.file = file;

    if (this.isValidFileForXpath) {
      this._filesService.getFileContent(this.file.id).subscribe((fileContent) => {
        this.xmlContent = formatter(fileContent, {
          collapseContent: true,
          indentation: '  ',
        });

        if (this._codeEditor) {
          this._codeEditor.codeMirror.setSize('100%', '100%');
        }
      });
    }

    if (this.isHtmlFile) {
      this._filesService.getFileContent(this.file.id).subscribe((fileContent) => {
        this.html = this._sanitizer.bypassSecurityTrustHtml(fileContent);
      });
    }
  }

  public file: IFileDocument;

  public canvasFilesLoaded: boolean;
  public html: SafeHtml;

  public icons = {
    fileExclamation: faFileCircleExclamation,
    spinner: faCircleNotch,
    fileSlash: faFileSlash,
  };

  public xmlContent = '';
  public xmlEditorOptions = {
    lineNumbers: true,
    smartIndent: true,
    tabSize: 2,
    readOnly: 'nocursor',
    dragDrop: false,
    mode: 'xml',
    foldGutter: true,
    lineWrapping: true,
  };

  constructor(private _filesService: FilesService, private _sanitizer: DomSanitizer, private _eventEmitterService: EventEmitterService) {}

  public get isValidFile(): boolean {
    return this.file && FilesHelper.isReaderSupported(this.file);
  }

  public get isValidFileForCanvas(): boolean {
    return this.file && FilesHelper.isCanvasSupported(this.file);
  }

  public get isValidFileForXpath(): boolean {
    return this.file && FilesHelper.isXml(this.file);
  }

  public get isHtmlFile(): boolean {
    return this.file && FilesHelper.isHtml(this.file);
  }

  private _getElementTreeXPath(element: Node): string {
    const paths = []; // Use nodeName (instead of localName)
    // so namespace prefix is included (if any).
    for (; element && element.nodeType == Node.ELEMENT_NODE; element = element.parentNode) {
      let index = 0;
      let hasFollowingSiblings = false;
      for (let sibling = element.previousSibling; sibling; sibling = sibling.previousSibling) {
        // Ignore document type declaration.
        if (sibling.nodeType == Node.DOCUMENT_TYPE_NODE) continue;

        if (sibling.nodeName == element.nodeName) ++index;
      }

      for (let sibling = element.nextSibling; sibling && !hasFollowingSiblings; sibling = sibling.nextSibling) {
        if (sibling.nodeName == element.nodeName) hasFollowingSiblings = true;
      }

      const tagName = (element['prefix'] ? String(element['prefix']) + ':' : '') + String(element['localName']);
      const pathIndex = index || hasFollowingSiblings ? '[' + String(index + 1) + ']' : '';
      paths.splice(0, 0, tagName + pathIndex);
    }

    return paths.length ? '/' + paths.join('/') : null;
  }

  public editorCursorActivity(event: CodeMirror.Editor): void {
    const linehandle = event.getLineHandle(event.getCursor().line);

    if (!linehandle) {
      return;
    }

    const elements = linehandle.text.match(/<[^>]*>/);

    if (!elements || linehandle.text === '' || linehandle.text.indexOf('<?xml') !== -1) {
      return;
    }

    // Por enquanto pega o primeiro elemento único
    // TODO: pegar o elemento que está selecionado
    const firstElement = elements[0].replace(/<|>/g, '');
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(this.xmlContent, 'text/xml');
    const element = xmlDoc.querySelector(firstElement);
    const xpath = this._getElementTreeXPath(element);

    this._eventEmitterService.get<ISmartCanvasClickedNodeEvent>('clicked-node-value').emit({
      xPath: xpath,
    });
  }
}
